<script setup>
import { HrExpansionItem } from '../primitives';

/**
 * Expandable section — a collapsible content panel with an M3 corner-radius shape scale.
 *
 * @status recommended
 * @group cards-dialogs
 * @useWhen Showing or hiding a block of secondary content under a clickable header row
 * @avoidWhen Navigation grouping in a side drawer — use HrNavGroup
 * @related HrNavGroup
 */
defineOptions({ name: 'HrExpandableSection' });

/**
 * @typedef {'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl'} HRExpandableSectionShape
 */
defineProps({
  /**
   * Corner radius of the container following the M3 shape scale.
   * @values none, xs, sm, md, lg, xl
   */
  shape: {
    type: String,
    default: 'xl',
    validator: (v) => ['none', 'xs', 'sm', 'md', 'lg', 'xl'].includes(v),
  },
});
</script>

<template>
  <hr-expansion-item
    :class="['hr-expandable-section', `hr-expandable-section--shape-${shape}`]"
    v-bind="$attrs"
  >
    <!-- @slot Expansion panel body content. -->
    <slot />
  </hr-expansion-item>
</template>

<style lang="scss" scoped>

/* Only applies to light/dark themes */

/* ── Container shape + clipping ──────────────────────────────────────────── */
/*
 * border-radius on the inner container so clipping applies to both the header
 * and content backgrounds. overflow: hidden clips square corners of child elements.
 */
.hr-expandable-section :deep(.q-expansion-item__container) {
  overflow: hidden;
  border-radius: var(--hr-expandable-radius);
}

.hr-expandable-section--shape-none { --hr-expandable-radius: var(--app-shape-corner-none); }
.hr-expandable-section--shape-xs   { --hr-expandable-radius: var(--app-shape-corner-extra-small); }
.hr-expandable-section--shape-sm   { --hr-expandable-radius: var(--app-shape-corner-small); }
.hr-expandable-section--shape-md   { --hr-expandable-radius: var(--app-shape-corner-medium); }
.hr-expandable-section--shape-lg   { --hr-expandable-radius: var(--app-shape-corner-large); }
.hr-expandable-section--shape-xl   { --hr-expandable-radius: var(--app-shape-corner-extra-large); }

/* ── Header typography ───────────────────────────────────────────────────── */
/* M3 content section headers: title-medium */
.hr-expandable-section :deep(.q-item__label) {
  font-size:      var(--app-type-title-medium-size);
  line-height:    var(--app-type-title-medium-line-height);
  font-weight:    var(--app-type-title-medium-weight);
  letter-spacing: var(--app-type-title-medium-tracking);
}

/* ── Header surface ──────────────────────────────────────────────────────── */
.hr-expandable-section :deep(.q-item) {
  background: var(--app-surface-container-high);
}

/* ── Content surface + padding ───────────────────────────────────────────── */
.hr-expandable-section :deep(.q-expansion-item__content) {
  background: var(--app-surface-container-low);
  padding: 16px;
}

</style>
